import {
  EnvelopeIcon,
  StarIcon,
  TicketRevenueIcon,
  TicketSoldIcon,
} from '@/components/Icons'
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@/components/ui/tooltip'
import { SectionCards, type DateFilterValues } from '@/components/section-cards'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { ArrowUpDown, ChevronRight, Loader } from 'lucide-react'
import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ATTENDEE_QUERY_KEYS } from '@/api/queryKeys'
import {
  fetchAttendees,
  getEventAdminStatistics,
  sendTicket,
  getGenerateEventReport,
  type reportType,
} from '@/api/attendeeApi'
import {
  formatGrowth,
  handleAxiosError,
  transformAttendeeData,
  safeDownloadFile,
} from '@/utils/helper'
import type { Attendee } from '@/schemas/attendee.schema'
import type { DisplayAttendee } from '../attendee/attendee-columns'
import { TableSkeleton } from '../TableSkeletons'
import { showSuccessToast } from '../ui/toast'
import useDebounce from '@/hooks/use-debounce'
import { useNavigate } from 'react-router-dom'
import { PATHROUTES } from '@/constants/path'
import {
  DASHBOARD_DATA_FETCH_TIMEOUT,
  SEARCH_DEBOUNCE_TIMEOUT,
} from '@/constants'
import { parseISO, format } from 'date-fns'
import { DatePicker } from '../ui/date-picker'
import type { SortingState } from '@tanstack/react-table'
import { attendeeSortinngColumnsNames } from '@/constants/attendee'

interface EventDashboardDataProps {
  searchTerm: string
}

interface IEventAdminStatItem {
  count: number
  percentageChange: number
}

interface ITopTicket {
  ticketName: string
  totalQuantitySold: number
}

type IEventAdminStatistics = Record<
  'ticketRevenue' | 'totalTicketsSold' | 'feeRevenue' | 'totalRevenue',
  IEventAdminStatItem
> & {
  top3Tickets?: ITopTicket[]
}

interface GetEventReportParams {
  reportType: reportType
  startDate?: string
  endDate?: string
}

const reportTypes = [
  { label: 'Revenue Summary', value: 'revenue' },
  { label: 'Daily Sales Report', value: 'daily_sales' },
  { label: 'Attendance Report', value: 'attendee' },
  { label: 'Ticket Type Analysis', value: 'ticket_analysis' },
]

function EventDashboardData({ searchTerm }: Readonly<EventDashboardDataProps>) {
  const [reportType, setReportType] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [resendingId, setResendingId] = useState<string | null>(null)
  const [startDateError, setStartDateError] = useState('')
  const [endDateError, setEndDateError] = useState('')
  const [sorting, setSorting] = useState<SortingState>([
    { id: attendeeSortinngColumnsNames.createdAt, desc: true },
  ])
  const navigate = useNavigate()
  const [dateFilters, setDateFilters] = useState<DateFilterValues>()

  const debouncedSearchTerm = useDebounce(searchTerm, SEARCH_DEBOUNCE_TIMEOUT)

  const { data: eventStatisticsData, isLoading: isEventStatisticsDataLoading } =
    useQuery({
      queryKey: [ATTENDEE_QUERY_KEYS.eventAdminStatistics, dateFilters],
      queryFn: () => {
        return getEventAdminStatistics({
          ...(dateFilters && {
            month: dateFilters.month,
            year: dateFilters.year,
          }),
        })
      },
      refetchInterval: DASHBOARD_DATA_FETCH_TIMEOUT,
      refetchIntervalInBackground: true,
    })

  const { data: attendeesData, isLoading: isAttendeesDataLoading } = useQuery({
    queryKey: [ATTENDEE_QUERY_KEYS.all, debouncedSearchTerm, sorting],
    queryFn: () =>
      fetchAttendees({
        limit: 15,
        sort: sorting,
        search: debouncedSearchTerm || undefined,
      }),
    refetchInterval: DASHBOARD_DATA_FETCH_TIMEOUT,
    refetchIntervalInBackground: true,
  })

  const { mutate: sendTicketMutation, isPending: sendingTicket } = useMutation({
    mutationFn: (transactionId: string) => sendTicket(transactionId),
    onSuccess: () => {
      showSuccessToast(`Ticket successfully re-sent to attendee.`)
      setResendingId(null)
    },
    onError: err => {
      handleAxiosError('Failed to resend ticket. Please try again.', err)
      setResendingId(null)
    },
  })

  const reportMutation = useMutation({
    mutationFn: (params: GetEventReportParams) =>
      getGenerateEventReport(params),
    onSuccess: blob =>
      safeDownloadFile({
        data: blob,
        fileName: `event_report_${reportType.toLowerCase()}.csv`,
      }),
    onError: err => {
      handleAxiosError('Failed to generate report. Please try again.', err)
    },
  })

  const eventAdminStatistics: IEventAdminStatistics = eventStatisticsData?.data

  const rawAttendees: Attendee[] = attendeesData?.data?.result || []
  const displayAttendee: DisplayAttendee[] = rawAttendees.map(
    transformAttendeeData
  )

  const handleSendTicketClick = (transactionId: string) => {
    setResendingId(transactionId)
    sendTicketMutation(transactionId)
  }

  const handleNavigateToAttendeeManagement = () => {
    navigate(PATHROUTES.DASHBOARD.ATTENDEE_MANAGEMENT)
  }

  const handleGenerateReport = () => {
    const params: GetEventReportParams = {
      reportType: reportType as reportType,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    }

    reportMutation.mutate(params)
  }

  const startDateValue = startDate ? parseISO(startDate) : undefined
  const endDateValue = endDate ? parseISO(endDate) : undefined

  const handleDateChange =
    (field: 'startDate' | 'endDate') => (picked: Date | undefined) => {
      setStartDateError('')
      setEndDateError('')

      const formattedDate = picked ? format(picked, 'yyyy-MM-dd') : ''
      if (field === 'startDate') {
        setStartDate(formattedDate)
        if (
          endDate &&
          formattedDate &&
          parseISO(formattedDate) >= parseISO(endDate)
        ) {
          setStartDateError('Start date must be before end date')
        }
      } else {
        setEndDate(formattedDate)
        if (
          startDate &&
          formattedDate &&
          parseISO(startDate) >= parseISO(formattedDate)
        ) {
          setEndDateError('End date must be after start date')
        }
      }
    }

  const kpiData = [
    {
      title: 'Total Tickets Sold',
      value: eventAdminStatistics?.totalTicketsSold.count,
      icon: <TicketSoldIcon className="text-white" />,
      cardType: 1 as const,
      growth: formatGrowth(
        eventAdminStatistics?.totalTicketsSold.percentageChange
      ),
    },
    {
      title: 'Ticket Revenue',
      value: eventAdminStatistics?.ticketRevenue.count,
      icon: <TicketRevenueIcon className="text-white" />,
      cardType: 2 as const,
      growth: formatGrowth(
        eventAdminStatistics?.ticketRevenue.percentageChange
      ),
    },
  ]

  const handleSort = (columnId: string) => {
    setSorting(prev => {
      const existing = prev.find(s => s.id === columnId)

      if (!existing) {
        return [{ id: columnId, desc: false }]
      } else {
        return [{ id: columnId, desc: !existing.desc }]
      }
    })
  }

  return (
    <div className="flex flex-col gap-6 pb-6" aria-label="Event Dashboard Data">
      {/* KPI Cards Section */}
      <section aria-label="Key Performance Indicators">
        <SectionCards
          isLoading={isEventStatisticsDataLoading}
          cards={kpiData}
          showFilters
          filterValues={dateFilters}
          onFilterChange={val => setDateFilters(val)}
          className="sm:grid-cols-2 xl:grid-cols-2"
        />
      </section>

      {/* Main Content Layout */}
      <div className="flex gap-6 px-4 lg:px-6 flex-wrap xl:flex-nowrap">
        {/* Left Side - Tables */}
        <div className="w-full xl:w-[70%] flex flex-col gap-6">
          {/* Recent Sales Table */}
          <section aria-label="Recent Sales Data" className="w-full">
            <div className="overflow-hidden">
              {/* Table Header */}
              <div className="flex items-center justify-between py-4">
                <h2 className="text-xl font-bold text-ss-neutral-950 leading-7">
                  Recent Sales
                </h2>
                <Button
                  variant="ghost"
                  className="text-ss-blue-800 text-sm font-semibold p-0 h-auto hover:bg-transparent"
                  aria-label="View all recent sales"
                  onClick={handleNavigateToAttendeeManagement}
                >
                  View All
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>

              {/* Table Container with Custom Scrollbar */}
              {isAttendeesDataLoading ? (
                <TableSkeleton rows={4} columns={6} />
              ) : (
                <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-ss-neutral-200 scrollbar-track-transparent h-96 bg-white rounded-xl border border-ss-neutral-50 overflow-hidden">
                  <TooltipProvider>
                    <Table
                      className="min-w-[800px] h-96"
                      sectionClassName="h-96"
                    >
                      <TableHeader className="sticky top-0">
                        <TableRow className="bg-ss-neutral-25 hover:bg-ss-neutral-25 border-ss-neutral-50">
                          <TableHead className="text-ss-neutral-950 font-medium text-xs leading-4 px-4 py-3 w-[100px] min-w-[100px] max-w-[100px]">
                            Name
                          </TableHead>
                          <TableHead className="text-ss-neutral-950 font-medium text-xs leading-4 px-4 py-3 w-[120px] min-w-[120px] max-w-[120px]">
                            Event Name
                          </TableHead>
                          <TableHead className="text-ss-neutral-950 font-medium text-xs leading-4 px-4 py-3 w-[100px] min-w-[100px] max-w-[100px]">
                            Email
                          </TableHead>
                          <TableHead className="text-ss-neutral-950 font-medium text-xs leading-4 px-4 py-3 w-[120px] min-w-[120px] max-w-[120px]">
                            <Button
                              className=" max-w-75"
                              variant="ghost"
                              onClick={() =>
                                handleSort(
                                  attendeeSortinngColumnsNames.createdAt
                                )
                              }
                            >
                              Purchase Date
                              <ArrowUpDown
                                className="ml-2 h-4 w-4"
                                aria-hidden="true"
                              />
                            </Button>
                          </TableHead>
                          <TableHead className="text-ss-neutral-950 font-medium text-xs leading-4 px-4 py-3 w-[110px] min-w-[110px] max-w-[110px]">
                            <Button
                              className="max-w-75"
                              variant="ghost"
                              onClick={() =>
                                handleSort(
                                  attendeeSortinngColumnsNames.totalAmount
                                )
                              }
                            >
                              Total Paid
                              <ArrowUpDown
                                className="ml-2 h-4 w-4"
                                aria-hidden="true"
                              />
                            </Button>
                          </TableHead>
                          <TableHead className="w-[30px] px-4 py-3"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {displayAttendee.map(sale => (
                          <TableRow
                            key={sale.id}
                            className="border-ss-neutral-50"
                          >
                            <TableCell className="font-semibold text-ss-neutral-950 text-sm leading-4.5  px-4 py-4 w-[100px] min-w-[100px] max-w-[100px]">
                              {sale.name}
                            </TableCell>
                            <TableCell className="text-ss-neutral-600 text-sm font-medium leading-4.5  px-4 py-4 truncate w-[100px] min-w-[100px] max-w-[100px]">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="block truncate">
                                    {sale.eventName}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {sale.eventName}
                                </TooltipContent>
                              </Tooltip>
                            </TableCell>
                            <TableCell className="text-ss-neutral-600 text-sm font-medium leading-4.5  px-4 py-4 truncate w-[100px] min-w-[100px] max-w-[100px]">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="block truncate">
                                    {sale.email}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>{sale.email}</TooltipContent>
                              </Tooltip>
                            </TableCell>
                            <TableCell className="text-ss-neutral-600 text-sm font-medium leading-4.5  px-4 py-4 truncate w-[100px] min-w-[100px] max-w-[100px]">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="block truncate">
                                    {sale.purchaseDate}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {sale.purchaseDate}
                                </TooltipContent>
                              </Tooltip>
                            </TableCell>
                            <TableCell className="px-4 py-4 w-[100px] min-w-[100px] max-w-[100px]">
                              <div className="flex flex-col">
                                <span className="text-ss-neutral-950 font-medium text-sm leading-4.5 ">
                                  $ {sale.baseAmount}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="px-4 py-4 w-[50px] min-w-[50px] max-w-[50px]">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    aria-label={`Contact ${sale.name}`}
                                    onClick={() =>
                                      handleSendTicketClick(sale.id)
                                    }
                                  >
                                    {sendingTicket &&
                                    resendingId === sale.id ? (
                                      <Loader
                                        className="size-5 animate-spin"
                                        aria-hidden="true"
                                      />
                                    ) : (
                                      <EnvelopeIcon className="h-5 w-5 text-ss-neutral-600" />
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Re-Send Ticket</TooltipContent>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TooltipProvider>
                </div>
              )}
            </div>
          </section>

          {/* Ticket Type Performance */}
          <section aria-label="Ticket Type Performance" className="w-full">
            <div className="flex flex-col gap-4">
              <h2 className="text-xl font-bold text-ss-neutral-950 leading-7">
                Ticket Type Performance
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {eventAdminStatistics?.top3Tickets &&
                  eventAdminStatistics?.top3Tickets.map(item => (
                    <div
                      key={item.ticketName}
                      className="bg-white rounded-xl border border-ss-neutral-50 p-5 flex items-center gap-3 min-h-[84px]"
                    >
                      <div className="flex-shrink-0 w-10 h-10 bg-ss-blue-50 rounded-full flex items-center justify-center">
                        <StarIcon className="h-5 w-5 text-black" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-ss-neutral-950 leading-[22px] truncate">
                          {item.ticketName}
                        </h3>
                      </div>
                      <div className="text-xl font-bold text-ss-neutral-950 text-right leading-7">
                        {item.totalQuantitySold}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </section>
        </div>

        {/* Right Side - Report Generation */}
        <div className="w-full xl:w-[30%]">
          <section aria-label="Generate Report" className="w-full h-full">
            <div className="flex flex-col gap-4 h-full">
              <h2 className="text-xl font-bold text-ss-neutral-950 leading-7">
                Generate Report
              </h2>

              <div className="bg-white rounded-xl border border-ss-neutral-50 p-4 flex-1">
                <div className="flex flex-col gap-4 justify-between flex-1 w-full h-full">
                  <div>
                    <div className="flex gap-4 mb-4">
                      {/* Responsive Field Group: full width on mobile, side-by-side on tablet, full width on desktop */}
                      <div className="flex flex-col gap-4 sm:flex-row sm:gap-4 xl:flex-col w-full">
                        {/* Report Type Selection */}
                        <div className="space-y-2 mb-0 w-full">
                          <Label
                            htmlFor="report-type"
                            className="text-sm font-semibold text-ss-neutral-950 leading-4.5 "
                          >
                            Report Type
                          </Label>
                          <Select
                            value={reportType}
                            onValueChange={setReportType}
                            aria-label="Select report type"
                          >
                            <SelectTrigger
                              size="lg"
                              id="report-type"
                              className="h-11 w-full bg-white font-normal leading-[22px] border-ss-neutral-200 text-sm"
                            >
                              <SelectValue placeholder="Select a report type" />
                            </SelectTrigger>
                            <SelectContent>
                              {reportTypes.map(type => (
                                <SelectItem key={type.value} value={type.value}>
                                  {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Start Date */}
                        <div className="space-y-2 mb-0 w-full">
                          <Label
                            htmlFor="start-date"
                            className="text-sm font-semibold text-ss-neutral-950 leading-4.5 "
                          >
                            Start Date
                          </Label>
                          <DatePicker
                            className={cn(
                              'h-11',
                              startDateError &&
                                'border-red-500 focus:border-red-500'
                            )}
                            value={startDateValue}
                            onChange={handleDateChange('startDate')}
                            placeholder="Select start date"
                            id="start-date"
                            aria-label="Select start date"
                            disableFutureDates={true}
                            fromYear={2020}
                            toYear={new Date().getFullYear()}
                          />
                          {startDateError && (
                            <p
                              className="text-sm text-red-600 mt-1"
                              role="alert"
                            >
                              {startDateError}
                            </p>
                          )}
                        </div>

                        {/* End Date */}
                        <div className="space-y-2 mb-0 w-full">
                          <Label
                            htmlFor="end-date"
                            className="text-sm font-semibold text-ss-neutral-950 leading-4.5 "
                          >
                            End Date
                          </Label>
                          <DatePicker
                            className={cn(
                              'h-11',
                              endDateError &&
                                'border-red-500 focus:border-red-500'
                            )}
                            value={endDateValue}
                            onChange={handleDateChange('endDate')}
                            placeholder="Select end date"
                            id="end-date"
                            aria-label="Select end date"
                            disableFutureDates={true}
                            fromYear={2020}
                            toYear={new Date().getFullYear()}
                          />
                          {endDateError && endDate && (
                            <p
                              className="text-sm text-red-600 mt-1"
                              role="alert"
                            >
                              {endDateError}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Generate Button */}
                  <Button
                    onClick={handleGenerateReport}
                    disabled={
                      reportMutation.isPending ||
                      !reportType ||
                      endDateError !== '' ||
                      startDateError !== ''
                    }
                    aria-label="Generate and download report (disabled - select options first)"
                  >
                    {reportMutation.isPending ? (
                      <>
                        <Loader className="h-4 w-4 mr-2 animate-spin" />
                        Generating Report...
                      </>
                    ) : (
                      'Generate & Download Report'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}

export default EventDashboardData
